/**
 * Login Page Component
 *
 * @author Claude
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Link,
  InputAdornment,
} from '@mui/material';
import { Helmet } from 'react-helmet-async';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import LoginIcon from '@mui/icons-material/Login';
import { useAuth } from '../hooks/useAuth';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import * as Yup from 'yup';

interface LoginFormValues {
  email: string;
  password: string;
}

const validationSchema = Yup.object({
  email: Yup.string().email('Please enter a valid email address').required('Email is required'),
  password: Yup.string().required('Password is required'),
});

/**
 * Login page with form-based authentication
 * @returns {JSX.Element} Login page component
 *
 * @a11y Uses semantic HTML with proper heading structure
 * @a11y Form fields have accessible labels
 * @a11y Error messages are announced to screen readers
 * @a11y Loading states are properly communicated
 */
export const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated, loading } = useAuth();
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);

  // Get the intended destination
  const from = (location.state as any)?.from?.pathname || '/dashboard';

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, from]);

  const formik = useFormik<LoginFormValues>({
    initialValues: {
      email: '',
      password: '',
    },
    validationSchema,
    onSubmit: async values => {
      try {
        setError(null);
        await login({
          email: values.email,
          password: values.password,
        });
        navigate(from, { replace: true });
      } catch (err: any) {
        setError(err.message || 'Login failed');
      }
    },
  });

  // Auto-focus email field on mount
  useEffect(() => {
    const emailField = document.getElementById('email');
    if (emailField) {
      emailField.focus();
    }
  }, []);

  return (
    <>
      <Helmet>
        <title>Login - Accessibility Maturity Reporter</title>
      </Helmet>

      <Box
        component="main"
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'background.default',
        }}
      >
        <Container maxWidth="sm">
          <Paper
            elevation={3}
            sx={{
              p: 4,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            {/* Title */}
            <Typography component="h1" variant="h4" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
              Sign In
            </Typography>

            {/* Error Alert */}
            {error && (
              <Alert severity="error" sx={{ width: '100%', mb: 3 }} role="alert">
                {error}
              </Alert>
            )}

            {/* Login Form */}
            <Box component="form" onSubmit={formik.handleSubmit} sx={{ width: '100%' }} noValidate>
              <TextField
                id="email"
                name="email"
                type="email"
                label="Email"
                fullWidth
                margin="normal"
                autoComplete="email"
                autoFocus
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                id="password"
                name="password"
                type="password"
                label="Password"
                fullWidth
                margin="normal"
                autoComplete="current-password"
                value={formik.values.password}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.password && Boolean(formik.errors.password)}
                helperText={formik.touched.password && formik.errors.password}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon />
                    </InputAdornment>
                  ),
                }}
              />

              {/* Help Link */}
              <Box sx={{ textAlign: 'right', mt: 1, mb: 2 }}>
                <Link
                  component={RouterLink}
                  to="/forgot-password"
                  variant="body2"
                  underline="hover"
                >
                  Forgot password?
                </Link>
              </Box>

              {/* Submit Button */}
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={formik.isSubmitting || loading}
                startIcon={formik.isSubmitting ? null : <LoginIcon />}
                sx={{ py: 1.5 }}
              >
                {formik.isSubmitting ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    Signing in...
                  </>
                ) : (
                  'Login'
                )}
              </Button>

              {/* Access Info */}
              <Box sx={{ textAlign: 'center', mt: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  Contact your administrator for access.
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Container>
      </Box>
    </>
  );
};

export default Login;
